import React, { useEffect } from 'react';

import { blobUrl } from 'constants/Common';

import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router';

import { useTheme } from 'components/theme/ThemeProvider';

import { useUrlQueryParams } from 'hooks/useUrlQueryParams';

import { VideoContentOverlay } from './VideoContentOverlay';

export const Layout = ({ children }: { children: React.ReactNode }) => {
    useTheme();
    const urlQueryParams = useUrlQueryParams();
    const redirectUrl = urlQueryParams.get('r');

    // The video panel competes for attention with the documents the user has to accept, so that
    // step gets the whole width. Its form brings its own card.
    const showVideoPanel = useLocation().pathname !== '/privacy-policy';

    // If there's a redirect URL and user is already authenticated, redirect
    useEffect(() => {
        if (redirectUrl) {
            const isAuthenticated = document.cookie.includes('.AspNetCore.Identity.Application');
            if (isAuthenticated) {
                window.location.href = redirectUrl;
            }
        }
    }, [redirectUrl]);

    const systemFontFamily = [
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        '"Noto Sans"',
        'sans-serif',
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
        '"Segoe UI Symbol"',
        '"Noto Color Emoji"',
    ].join(', ');

    return (
        <div
            className="tw-flex tw-justify-center tw-min-h-screen tw-bg-neutral-body tw-px-24 tw-text"
            style={{ fontFamily: systemFontFamily }}
        >
            <div
                className={`tw-grid tw-grid-cols-1 ${
                    showVideoPanel ? 'min-[1000px]:tw-grid-cols-2' : ''
                } tw-w-full tw-items-center tw-justify-items-center tw-max-w-[1440px]`}
            >
                {/* Left panel: login form */}
                <div
                    className={
                        showVideoPanel
                            ? 'lg:tw-w-auto lg:tw-max-w-none tw-max-w-[400px] tw-w-full'
                            : 'tw-max-w-[640px] tw-w-full'
                    }
                >
                    <div className="tw-flex tw-flex-col tw-justify-between tw-items-center tw-shrink-0">
                        <div
                            className={
                                showVideoPanel
                                    ? 'tw-border tw-rounded tw-shadow-xs tw-p-24 lg:tw-w-[400px] tw-w-full lg:tw-mt-48'
                                    : 'tw-w-full lg:tw-mt-48'
                            }
                        >
                            <div className="tw-rounded tw-bg-neutral-variant-0 tw-p-24 tw-flex tw-justify-center tw-mb-24">
                                <img
                                    src={`${blobUrl}/common/logos/standard-horizontal.png`}
                                    alt="SalesScreen"
                                    className="tw-h-32 dark:tw-hidden"
                                />
                                <img
                                    src={`${blobUrl}/common/logos/inverted-horizontal.png`}
                                    alt="SalesScreen"
                                    className="tw-h-32 dark:tw-block tw-hidden"
                                />
                            </div>
                            {children}
                        </div>
                        <footer className="tw-text-neutral-subdued tw-text-s tw-mt-12">
                            <FormattedMessage
                                id="login.footer.rights"
                                defaultMessage="© {year} SalesScreen. All rights reserved."
                                values={{ year: new Date().getFullYear() }}
                            />
                        </footer>
                    </div>
                </div>

                {/* Right panel: video (hidden on small screens) */}
                {showVideoPanel && (
                    <div className="tw-hidden min-[1000px]:tw-flex tw-rounded-[16px] tw-w-full tw-h-[85vh] tw-min-h-[500px] tw-justify-center tw-items-center tw-overflow-hidden tw-mb-8">
                        <div className="tw-relative tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center">
                            <video
                                src={`${blobUrl}login/hiking-video-bg.mp4`}
                                poster={`${blobUrl}/login/hiking-badges.jpg`}
                                autoPlay
                                loop
                                muted
                                playsInline
                                className="tw-w-full tw-h-full tw-object-cover tw-rounded-2xl"
                            />
                            {/* <img
                                src={`${blobUrl}/login/hiking-badges.jpg`}
                                alt="Overlay"
                                className="tw-absolute tw-inset-0 tw-w-full tw-h-full tw-object-cover"
                            /> */}
                            <div
                                className="tw-absolute tw-top-0 tw-left-0 tw-h-full tw-w-full"
                                style={{
                                    background:
                                        'linear-gradient(to top, rgba(0,0,0,0.8), rgba(0,0,0,0.5) 50%, transparent)',
                                }}
                            />
                            <VideoContentOverlay />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
